import { Button } from "@shared-ui/button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import {
	GstRatePreviewPanel,
	GstRepriceSummary,
} from "@/features/catalog/components/gst-rate-preview";
import { formatRate, formatRateDay } from "@/features/catalog/lib/gst-rates";
import {
	cancelGstRate,
	type GstRate,
	type GstRepriceResult,
	previewGstRateChange,
	repriceGstPrefix,
} from "@/features/catalog/services/gst-rates-api.service";
import { apiErrorMessage } from "@/lib/api-error";

const TITLE_ID = "gst-cancel-dialog-title";

/**
 * Cancel a rate that has not started (#514, R8): the row is deleted and the
 * rate it closed, if any, becomes open-ended again. Names both rows before
 * anything happens, because the second one is a row the operator did not open.
 */
export function GstCancelDialog({
	rate,
	previous,
	onClose,
	onChanged,
}: {
	rate: GstRate;
	/** The row before it in the chain, if any. */
	previous: GstRate | null;
	onClose: () => void;
	onChanged: () => void;
}) {
	const [result, setResult] = useState<GstRepriceResult | null>(null);
	const preview = useQuery({
		queryKey: ["admin", "catalog", "gst-rates", "cancel-preview", rate.id],
		queryFn: () => previewGstRateChange({ kind: "cancel", id: rate.id }),
		retry: false,
	});
	const reopens =
		previous && previous.effectiveTo === rate.effectiveFrom ? previous : null;

	const cancel = useMutation({
		mutationFn: () =>
			// Offered only once the preview has loaded, so its latest row is the
			// one this dialog showed.
			cancelGstRate(rate.id, preview.data?.latestRateId ?? null),
		onSuccess: (data) => {
			setResult(data.reprojection);
			onChanged();
		},
	});
	const reprice = useMutation({
		mutationFn: () => repriceGstPrefix(rate.hsnPrefix),
		onSuccess: (data) => {
			setResult(data);
			onChanged();
		},
	});
	const busy = cancel.isPending || reprice.isPending;

	return (
		<Dialog
			open
			titleId={TITLE_ID}
			onClose={() => {
				if (!busy) onClose();
			}}
			className="grid max-h-[90vh] w-full max-w-2xl gap-4 overflow-y-auto rounded-3xl border bg-white p-5 shadow-xl"
		>
			<h2 id={TITLE_ID} className="text-lg font-semibold">
				Cancel {rate.hsnPrefix} at {formatRate(rate.rateBp)} from{" "}
				{formatRateDay(rate.effectiveFrom)}
			</h2>
			<div aria-live="polite" className="grid gap-3 text-sm">
				{result ? (
					<GstRepriceSummary
						headline={`Cancelled ${rate.hsnPrefix} at ${formatRate(rate.rateBp)}.`}
						result={result}
						repricing={reprice.isPending}
						error={
							reprice.isError
								? apiErrorMessage(reprice.error, "Could not re-price.")
								: null
						}
						onRepriceRemaining={() => reprice.mutate()}
					/>
				) : (
					<>
						<p>
							This rate has not started, so it is deleted.{" "}
							{reopens
								? `${rate.hsnPrefix} at ${formatRate(reopens.rateBp)} from ${formatRateDay(reopens.effectiveFrom)} becomes open-ended again.`
								: "No other rate changes."}
						</p>
						{preview.isPending ? (
							<p className="text-muted-foreground">Working out the effect…</p>
						) : preview.isError ? (
							<p role="alert" className="text-red-700">
								{apiErrorMessage(
									preview.error,
									"Could not work out the effect of cancelling.",
								)}
							</p>
						) : (
							<GstRatePreviewPanel preview={preview.data} newRateBp={null} />
						)}
						{cancel.isError ? (
							<p role="alert" className="text-red-700">
								{apiErrorMessage(cancel.error, "Could not cancel this rate.")}
							</p>
						) : null}
					</>
				)}
			</div>
			<div className="flex justify-end gap-2 border-t pt-3">
				{result ? (
					<Button onClick={onClose} disabled={busy}>
						Done
					</Button>
				) : (
					<>
						<Button variant="ghost" onClick={onClose} disabled={busy}>
							Keep it
						</Button>
						<Button
							variant="destructive"
							onClick={() => cancel.mutate()}
							disabled={busy || !preview.isSuccess}
						>
							{cancel.isPending ? "Cancelling…" : "Cancel this rate"}
						</Button>
					</>
				)}
			</div>
		</Dialog>
	);
}
